import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import TopBar from "@/components/TopBar";
import BottomNav from "@/components/BottomNav";
import CategoryBadge from "@/components/CategoryBadge";
import { AlertCircle, MapPin, Wallet, Calendar, CheckCircle2, BarChart3, Headset, Mail, Star, Inbox, Send, ShieldCheck } from "lucide-react";
import { CATEGORIES, CATEGORY_ICONS, DISTRICTS, MAX_MERCHANT_CATEGORIES, SUPPORT_EMAIL, FULL_ACTIVITIES, VET_ACTIVITIES } from "@/lib/constants";
import { greet } from "@/lib/name";
import { toast } from "sonner";

function Toggle({ checked, onChange, testId, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-testid={testId}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`hk-switch ${checked ? "bg-emerald-600" : "bg-zinc-300"} ${disabled ? "opacity-50" : ""}`}
    >
      <span className={`hk-switch-thumb ${checked ? "ltr:translate-x-6 rtl:-translate-x-6" : "ltr:translate-x-1 rtl:-translate-x-1"}`} />
    </button>
  );
}

export default function MerchantDashboard() {
  const { user, refresh } = useAuth();
  const { t, tCat, tAct, tCity, lang } = useLang();
  const [feed, setFeed] = useState([]);
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const available = user?.availability !== false;
  const coverage = user?.coverage || "full_city";
  const myDistricts = user?.districts || [];
  const myCats = user?.categories || [];
  const cityDistricts = DISTRICTS[user?.city] || [];
  const myActs = user?.activities || [];
  const pendingActs = user?.pending_verification_activities || [];

  const loadFeed = () => {
    if (!user?.approved) { setLoading(false); return; }
    api.get("/requests/feed")
      .then(r => setFeed(r.data))
      .catch(e => setErr(errMsg(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadFeed();
    if (user?.approved) {
      api.get("/merchant/analytics").then(r => setStats(r.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.approved, user?.availability, user?.coverage, JSON.stringify(user?.districts), JSON.stringify(user?.categories)]);

  const saveProfile = async (patch) => {
    setSaving(true);
    try {
      await api.put("/merchant/profile", patch);
      await refresh();
      toast.success(t("profileSaved"));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleAvailability = (val) => saveProfile({ availability: val });

  const [pendingCoverage, setPendingCoverage] = useState(false);
  const [draftDistricts, setDraftDistricts] = useState(myDistricts);
  useEffect(() => { setDraftDistricts(myDistricts); }, [JSON.stringify(myDistricts)]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleDraftDistrict = (d) => {
    setDraftDistricts((prev) => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
  };

  const saveDistricts = async () => {
    if (draftDistricts.length === 0) { toast.error(t("selectDistrict")); return; }
    await saveProfile({ coverage: "districts", districts: draftDistricts });
    setPendingCoverage(false);
  };

  const toggleCat = (c) => {
    let next;
    if (myCats.includes(c)) {
      next = myCats.filter(x => x !== c);
      if (next.length === 0) { toast.error(t("selectAtLeastOne")); return; }
    } else {
      if (myCats.length >= MAX_MERCHANT_CATEGORIES) { toast.error(t("maxTwoCategories")); return; }
      next = [...myCats, c];
    }
    saveProfile({ categories: next });
  };

  // ---- Services (specific full activities) manager ----
  const selectedActs = [...myActs, ...pendingActs];
  const [draftActs, setDraftActs] = useState(selectedActs);
  useEffect(() => { setDraftActs([...myActs, ...pendingActs]); }, [JSON.stringify(myActs), JSON.stringify(pendingActs)]); // eslint-disable-line react-hooks/exhaustive-deps
  const actsDirty = JSON.stringify([...draftActs].sort()) !== JSON.stringify([...selectedActs].sort());
  const toggleAct = (a) => setDraftActs((prev) => prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]);
  const saveActs = async () => {
    if (draftActs.length === 0) { toast.error(t("selectAtLeastOneActivity")); return; }
    await saveProfile({ activities: draftActs });
  };

  const showDistrictPicker = pendingCoverage || coverage === "districts";

  return (
    <div className="app-shell pb-24" data-testid="merchant-dashboard">
      <TopBar title={t("home")} back={false} />

      {/* Welcome hero + availability */}
      <div className="px-5 pt-5">
        <div className="rounded-3xl band-deep text-white p-5 relative overflow-hidden rise">
          <div className="absolute -top-8 -end-8 h-32 w-32 rounded-full bg-white/5" />
          <p className="text-emerald-200/80 text-xs font-semibold">Hustlers KSA</p>
          <h1 className="font-display text-2xl font-extrabold mt-1" data-testid="merchant-welcome-name">{greet(user, lang, { business: true })}</h1>
          <p className="text-emerald-100/70 text-sm mt-1">{tCity(user?.city)} · {myCats.map(tCat).join(" · ")}</p>
          <p className="text-emerald-100/60 text-xs mt-1">{t("manageSub")}</p>

          {user?.approved && (
            <div className="mt-4 flex items-center justify-between rounded-2xl bg-white/10 backdrop-blur px-4 py-3" data-testid="availability-box">
              <div>
                <div className="text-sm font-bold">{available ? t("availableForRequests") : t("notAvailableNow")}</div>
                <div className="text-[11px] text-emerald-100/60 mt-0.5">{t("availabilityHint")}</div>
              </div>
              <Toggle checked={available} onChange={toggleAvailability} testId="availability-toggle" disabled={saving} />
            </div>
          )}
        </div>

        {!user?.approved && (
          <div className="mt-4 card-surface p-4 bg-amber-50 border-amber-200" data-testid="pending-banner">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-700 mt-0.5" />
              <div>
                <div className="font-semibold text-amber-900">{t("needApproval")}</div>
                <div className="text-sm text-amber-800 mt-0.5">{t("pendingApproval")}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Stats */}
      {user?.approved && (
        <div className="px-5 mt-5 rise rise-1">
          <div className="grid grid-cols-4 gap-2" data-testid="merchant-stats">
            {[
              { label: t("statNewRequests"), value: feed.filter(f => !f.already_offered).length, icon: Inbox },
              { label: t("statMyOffers"), value: stats?.total_offers ?? "—", icon: Send },
              { label: t("statAccepted"), value: stats?.accepted ?? "—", icon: CheckCircle2 },
              { label: t("statRating"), value: stats?.rating ?? "—", icon: Star },
            ].map((s, i) => (
              <div key={i} className="card-surface p-3 text-center">
                <s.icon className="h-4 w-4 text-emerald-600 mx-auto" />
                <div className="font-display text-lg font-extrabold text-zinc-900 mt-1">{s.value}</div>
                <div className="text-[10px] text-zinc-500 leading-tight">{s.label}</div>
              </div>
            ))}
          </div>
          <Link to="/merchant/analytics" data-testid="cta-analytics" className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:underline underline-offset-4">
            <BarChart3 className="h-3.5 w-3.5" /> {t("analytics")}
          </Link>
        </div>
      )}

      {/* New matching requests */}
      {user?.approved && (
        <div className="px-5 mt-6 rise rise-2">
          <div className="flex items-baseline justify-between">
            <div>
              <h3 className="font-display text-lg font-extrabold text-[#12211D]">{t("newRequestsTitle")}</h3>
              <p className="text-xs text-zinc-500">{t("newRequestsSub")}</p>
            </div>
            <Link to="/merchant/feed" data-testid="cta-feed" className="text-xs font-semibold text-emerald-700 hover:underline underline-offset-4">{t("viewAll")} ({feed.length})</Link>
          </div>
          {err && <div className="mt-3 text-sm text-red-600 bg-red-50 rounded-lg p-3" role="alert">{err}</div>}
          {loading && <div className="mt-4 text-sm text-zinc-500">{t("loadingLabel")}</div>}
          {!loading && !available && (
            <div className="mt-4 card-surface p-5 text-sm text-zinc-600" data-testid="availability-off-note">{t("availabilityOffFeed")}</div>
          )}
          {!loading && available && feed.length === 0 && <div className="mt-4 card-surface p-5 text-sm text-zinc-600" data-testid="feed-empty">{t("noFeed")}</div>}
          <div className="mt-4 space-y-3">
            {available && feed.slice(0, 5).map((r) => (
              <div key={r.id} className="card-surface p-4" data-testid={`feed-card-${r.id}`}>
                <div className="flex gap-3 items-start">
                  <CategoryBadge category={r.category} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold text-zinc-900 truncate">
                        {tCat(r.category)}{r.activity ? ` · ${tAct(r.activity)}` : ""}
                      </div>
                      {r.already_offered && (
                        <span className="pill bg-emerald-50 text-emerald-700"><CheckCircle2 className="h-3 w-3" />{t("alreadyOffered")}</span>
                      )}
                    </div>
                    <p className="text-sm text-zinc-600 line-clamp-2 mt-0.5">{r.description}</p>
                    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-500">
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{tCity(r.city)}{r.district ? ` — ${r.district}` : ""}</span>
                      <span className="inline-flex items-center gap-1"><Wallet className="h-3 w-3" />{r.budget} {t("saudiRiyal")}</span>
                      <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{r.deadline}</span>
                    </div>
                  </div>
                </div>
                {!r.already_offered && (
                  <Link to={`/merchant/request/${r.id}`} data-testid={`submit-offer-${r.id}`} className="mt-3 btn-primary w-full !py-2.5 text-sm">
                    {t("submitOffer")}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Coverage area */}
      <div className="px-5 mt-7 rise rise-3">
        <div className="card-surface p-4" data-testid="coverage-card">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-emerald-600" />
            <h3 className="font-display text-base font-extrabold text-[#12211D]">{t("coverageTitle")}</h3>
          </div>
          <p className="text-xs text-zinc-500 mt-1">{t("coverageSub")}</p>
          <div className="mt-3 space-y-2">
            <button
              type="button"
              data-testid="coverage-full-city"
              onClick={() => { setPendingCoverage(false); saveProfile({ coverage: "full_city", districts: [] }); }}
              className={`w-full flex items-center gap-3 rounded-xl border px-3 py-3 text-start transition ${coverage === "full_city" && !pendingCoverage ? "border-emerald-600 bg-emerald-50" : "border-zinc-200 bg-white hover:border-zinc-300"}`}
            >
              <span className={`h-4 w-4 rounded-full border-2 grid place-items-center shrink-0 ${coverage === "full_city" && !pendingCoverage ? "border-emerald-600" : "border-zinc-300"}`}>
                {coverage === "full_city" && !pendingCoverage && <span className="h-2 w-2 rounded-full bg-emerald-600" />}
              </span>
              <span>
                <span className="block text-sm font-bold text-zinc-900">{t("fullCity")}</span>
                <span className="block text-[11px] text-zinc-500">{t("fullCitySub")}</span>
              </span>
            </button>
            <button
              type="button"
              data-testid="coverage-districts"
              onClick={() => setPendingCoverage(true)}
              disabled={cityDistricts.length === 0}
              className={`w-full flex items-center gap-3 rounded-xl border px-3 py-3 text-start transition disabled:opacity-50 ${showDistrictPicker ? "border-emerald-600 bg-emerald-50" : "border-zinc-200 bg-white hover:border-zinc-300"}`}
            >
              <span className={`h-4 w-4 rounded-full border-2 grid place-items-center shrink-0 ${showDistrictPicker ? "border-emerald-600" : "border-zinc-300"}`}>
                {showDistrictPicker && <span className="h-2 w-2 rounded-full bg-emerald-600" />}
              </span>
              <span>
                <span className="block text-sm font-bold text-zinc-900">{t("specificDistricts")}</span>
                <span className="block text-[11px] text-zinc-500">{t("specificDistrictsSub")}</span>
              </span>
            </button>
          </div>

          {showDistrictPicker && cityDistricts.length > 0 && (
            <div className="mt-3" data-testid="district-picker">
              <p className="label-caps mb-2">{t("selectedDistricts")} ({draftDistricts.length})</p>
              <div className="flex flex-wrap gap-1.5 max-h-44 overflow-y-auto pe-1">
                {cityDistricts.map((d) => {
                  const sel = draftDistricts.includes(d);
                  return (
                    <button
                      type="button"
                      key={d}
                      data-testid={`district-chip-${d}`}
                      onClick={() => toggleDraftDistrict(d)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${sel ? "border-emerald-600 bg-emerald-600 text-white" : "border-zinc-200 bg-white text-zinc-700 hover:border-emerald-300"}`}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                data-testid="save-districts"
                onClick={saveDistricts}
                disabled={saving || draftDistricts.length === 0}
                className="btn-primary w-full !py-2.5 text-sm mt-3"
              >
                {saving ? "..." : t("save")}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Service categories (max 2) */}
      <div className="px-5 mt-4 rise rise-4">
        <div className="card-surface p-4" data-testid="categories-card">
          <h3 className="font-display text-base font-extrabold text-[#12211D]">{t("myCategoriesTitle")}</h3>
          <p className="text-xs text-zinc-500 mt-1">{t("myCategoriesSub")}</p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {CATEGORIES.map((c) => {
              const Icon = CATEGORY_ICONS[c];
              const sel = myCats.includes(c);
              const disabled = !sel && myCats.length >= MAX_MERCHANT_CATEGORIES;
              return (
                <button
                  type="button"
                  key={c}
                  data-testid={`merchant-cat-${c}`}
                  onClick={() => toggleCat(c)}
                  disabled={saving}
                  className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-xs font-medium transition ${
                    sel ? "border-emerald-600 bg-emerald-50 text-emerald-700" : disabled ? "border-zinc-100 bg-zinc-50 text-zinc-400" : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300"
                  }`}
                >
                  <Icon className="h-4 w-4 shrink-0" />{tCat(c)}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Services — specific full activities within selected categories */}
      <div className="px-5 mt-4">
        <div className="card-surface p-4" data-testid="activities-card">
          <h3 className="font-display text-base font-extrabold text-[#12211D]">{t("myActivitiesTitle")}</h3>
          <p className="text-xs text-zinc-500 mt-1">{t("myActivitiesSub")}</p>
          {myCats.includes("pets") && (
            <p className="mt-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-[11px] font-medium px-3 py-2" data-testid="vet-notice-merchant">
              {t("vetNotice")}
            </p>
          )}
          {myCats.includes("moving_transport") && (
            <p className="mt-3 rounded-lg bg-sky-50 border border-sky-200 text-sky-900 text-[11px] font-medium px-3 py-2" data-testid="transport-docs-notice">
              {t("transportDocsNotice")}
            </p>
          )}
          {myCats.map((cat) => (
            <div key={cat} className="mt-3" data-testid={`activities-group-${cat}`}>
              <p className="label-caps mb-2">{tCat(cat)}</p>
              <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto pe-1">
                {(FULL_ACTIVITIES[cat] || []).map((a) => {
                  const sel = draftActs.includes(a);
                  const isVetPending = sel && VET_ACTIVITIES.includes(a) && !user?.vet_license_verified;
                  return (
                    <button
                      type="button"
                      key={a}
                      data-testid={`merchant-act-${a}`}
                      onClick={() => toggleAct(a)}
                      disabled={saving}
                      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                        isVetPending
                          ? "border-amber-500 bg-amber-100 text-amber-900"
                          : sel
                            ? "border-emerald-600 bg-emerald-600 text-white"
                            : "border-zinc-200 bg-white text-zinc-700 hover:border-emerald-300"
                      }`}
                    >
                      {tAct(a)}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {pendingActs.length > 0 && (
            <p className="mt-3 flex items-start gap-1.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-[11px] font-medium px-3 py-2" data-testid="pending-verification-note">
              <ShieldCheck className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true" />
              <span>{t("pendingVerification")}: {pendingActs.map(tAct).join(lang === "ar" ? "، " : ", ")}</span>
            </p>
          )}
          <button
            type="button"
            data-testid="save-activities"
            onClick={saveActs}
            disabled={saving || !actsDirty}
            className="btn-primary w-full !py-2.5 text-sm mt-4 disabled:opacity-50"
          >
            {saving ? t("loadingLabel") : t("save")}
          </button>
        </div>
      </div>

      {/* Support card */}
      <div className="px-5 mt-4">
        <div className="card-surface p-4 flex items-center gap-3" data-testid="merchant-support-card">
          <div className="h-11 w-11 rounded-full bg-emerald-50 text-emerald-700 grid place-items-center shrink-0">
            <Headset className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-zinc-900 text-sm">{t("needHelp")}</div>
            <div className="text-xs text-zinc-500 mt-0.5">{t("supportReady")}</div>
          </div>
          <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="merchant-support-email" className="inline-flex items-center gap-1.5 rounded-full border border-emerald-600 text-emerald-700 px-3.5 py-2 text-xs font-bold hover:bg-emerald-50 transition">
            <Mail className="h-3.5 w-3.5" /> {t("contactSupport")}
          </a>
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
